/**
 * useLensROAS — fetch SSP-fed ROAS for a wave with graceful degradation.
 *
 * Why a dedicated hook
 * --------------------
 * The brand-lenses proxy returns an envelope `{ok, reason, data}` where
 * `ok=false` is a NORMAL state (SSP returns http_404 when the wave hasn't
 * received any conversion events yet — most early-stage waves). Every
 * consuming component needs to handle three states:
 *
 *   • Loading       — initial fetch in flight
 *   • Has data      — render real ROAS
 *   • No data yet   — render placeholder (NOT an error)
 *
 * This hook bundles all three so consuming components stay clean.
 *
 * Return shape
 * ------------
 *   { roas, revenue, spend, conversions, hasData, isLoading, reason }
 *
 * `reason` exposes the underlying envelope reason for debugging — UI
 * code shouldn't switch on it, but it's useful for "Why is this empty?"
 * tooltips in dev / sandbox mode.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function useLensROAS(waveId) {
  const [state, setState] = useState({
    roas: null, revenue: null, spend: null, conversions: null,
    hasData: false, isLoading: true, reason: null,
  });

  useEffect(() => {
    let cancelled = false;
    if (!waveId) {
      setState(s => ({ ...s, isLoading: false }));
      return;
    }
    (async () => {
      try {
        const { data: envelope } = await axios.get(
          `${API}/api/brand/lenses/waves/${waveId}/roas`,
          { withCredentials: true },
        );
        if (cancelled) return;
        if (envelope.ok && envelope.data) {
          const d = envelope.data;
          setState({
            roas:        d.roas ?? null,
            revenue:     d.revenue_usd ?? null,
            spend:       d.spend_usd ?? null,
            conversions: d.conversion_count ?? null,
            hasData:     true,
            isLoading:   false,
            reason:      null,
          });
        } else {
          setState(s => ({ ...s, isLoading: false, reason: envelope.reason || 'unknown' }));
        }
      } catch (e) {
        if (cancelled) return;
        setState(s => ({ ...s, isLoading: false, reason: 'request_failed' }));
      }
    })();
    return () => { cancelled = true; };
  }, [waveId]);

  return state;
}
